import React from 'react';

import { LOAN_REQUEST_OFFER_SOURCES, OFFER_ACCEPTANCE_STATUSES } from 'cpool-constants';
import type { SchemaLoanRequestResolver_Get } from 'cpool-tables/backend-resolvers';
import { isEuropaceOfferAcceptable } from 'cpool-utils';
import { useTranslation } from 'react-i18next';
import { LuArrowRight, LuCheck, LuClock, LuInfo, LuX } from 'react-icons/lu';

import type { OfferSource } from './types';
import { OfferExtraInfoDialog } from '~/features/leadDetails/components/BankCreditRequest/NewCreditRequest/RequestConfiguration/OfferExtraInfo/OfferExtraInfoDialog';
import { useOfferExtraInfoState } from '~/features/leadDetails/components/BankCreditRequest/NewCreditRequest/RequestConfiguration/OfferExtraInfo/useOfferExtraInfoState';
import { useAcceptOffer } from '~/features/leadDetails/hooks/useAcceptOffer';
import { useEuropaceProcessId } from '~/features/leadDetails/hooks/useEuropaceProcessId.ts';
import { useOfferAcceptanceStatusByOfferId } from '~/features/leadDetails/hooks/useOfferAcceptanceStatusByOfferId';
import { cn } from '~/utils/cnTailwind.ts';

type Props = {
    currentRequestId?: string;
    lead: SchemaLoanRequestResolver_Get;
    isOdd: boolean;
    routeControlled?: boolean;
} & OfferSource;

export const OfferAcceptButton: React.FC<Props> = ({
    currentRequestId,
    isOdd,
    lead,
    offer,
    routeControlled = false,
    source,
}) => {
    const { t } = useTranslation();
    const {
        isOpen: isOpenInfo,
        isRouteControlled,
        setOpen: setIsOpenInfo,
    } = useOfferExtraInfoState(offer.id, routeControlled);

    const { data: europaceProcessId } = useEuropaceProcessId(lead.id);
    const { data: acceptanceStatus } = useOfferAcceptanceStatusByOfferId({
        loanRequestId: lead.id,
        offerId: offer.id,
        source,
    });

    let isAcceptable = false;
    if (source === LOAN_REQUEST_OFFER_SOURCES.EUROPACE) {
        isAcceptable = isEuropaceOfferAcceptable({
            completenessStatus: offer.vollstaendigkeit?.status,
            feasibilityStatus: offer.machbarkeit?.status,
        });
    }

    const acceptOffer = useAcceptOffer({
        currentRequestId,
        europaceProcessId: source === LOAN_REQUEST_OFFER_SOURCES.EUROPACE ? europaceProcessId : undefined,
        lead,
        source,
    });

    if (!isAcceptable) {
        return (
            <div className="items-center justify-center flex">
                <button
                    className={
                        'text-red-new font-medium text-xs flex items-center justify-center text-center w-36 rounded-md px-3 py-2 space-x-1 gap-1 bg-red-light'
                    }
                    onClick={() => setIsOpenInfo(true)}
                >
                    <LuInfo className="size-4" />
                    {t('BANK_CREDIT_REQUEST.OFFERS_TABLE.OFFER_ACCEPTANCE_IMPOSSIBLE')}
                </button>

                {!isRouteControlled && source === LOAN_REQUEST_OFFER_SOURCES.EUROPACE && (
                    <OfferExtraInfoDialog offer={offer} isOpen={isOpenInfo} setIsOpen={setIsOpenInfo} source={source} />
                )}
            </div>
        );
    }
    if (acceptanceStatus === OFFER_ACCEPTANCE_STATUSES.TIMEOUT) {
        return (
            <div className="items-center justify-center flex">
                <div className="text-white font-medium text-xs flex items-center justify-center text-center w-36 rounded-md px-3 py-2 bg-gray-300 space-x-1">
                    <LuClock className="size-4" />
                    <span>{t('BANK_CREDIT_REQUEST.OFFERS_TABLE.OFFER_ACCEPTANCE_TIMEOUT')}</span>
                </div>
            </div>
        );
    }
    if (acceptanceStatus === OFFER_ACCEPTANCE_STATUSES.PENDING) {
        return (
            <div className="items-center justify-center flex">
                <div className="text-white font-medium text-xs flex items-center justify-center text-center w-36 rounded-md px-3 py-2 bg-alert-success space-x-1">
                    {t('BANK_CREDIT_REQUEST.OFFERS_TABLE.OFFER_ACCEPTANCE_PENDING')}
                </div>
            </div>
        );
    }
    if (acceptanceStatus === OFFER_ACCEPTANCE_STATUSES.FAILURE) {
        return (
            <div className="items-center justify-center flex">
                <div
                    className={cn(
                        'text-red-new font-medium text-xs flex items-center justify-center text-center gap-1 w-36 rounded-md px-3 py-1.5 space-x-1',
                        isOdd ? 'bg-red-light' : 'bg-red-dark',
                    )}
                >
                    <LuX className="size-4" />
                    {t('BANK_CREDIT_REQUEST.OFFERS_TABLE.OFFER_ACCEPTANCE_FAILED')}
                </div>
            </div>
        );
    }
    if (acceptanceStatus === OFFER_ACCEPTANCE_STATUSES.SUCCESS) {
        return (
            <div className="items-center justify-center flex">
                <div
                    className={cn(
                        'text-green font-medium text-xs flex items-center justify-center text-center gap-1 w-36 rounded-md px-3 py-2 bg-green-light space-x-1',
                        isOdd ? 'bg-green-light' : 'bg-green-dark',
                    )}
                >
                    <LuCheck className="size-4" />
                    {t('BANK_CREDIT_REQUEST.OFFERS_TABLE.OFFER_ACCEPTANCE_SUCCESS')}
                </div>
            </div>
        );
    }

    return (
        <div className="items-center justify-center flex">
            <button
                className="text-white font-medium text-xs flex items-center justify-center text-center w-36 rounded-md px-3 py-1.5 bg-alert-success space-x-1"
                onClick={() => {
                    acceptOffer.mutate(offer);
                }}
            >
                <LuArrowRight className="size-4" />
                <span className="mt-0.5">{t('BANK_CREDIT_REQUEST.OFFERS_TABLE.REQUEST_BANK_ACCEPTANCE')}</span>
            </button>
        </div>
    );
};
